/**
 * AppV2 — Shell principal de la V2 Esprit Padel OS.
 *
 * Coexiste avec App.jsx (V1) : ces deux composants ne tournent jamais ensemble.
 * Le toggle USE_V2_SHELL dans src/index.js détermine lequel est rendu.
 *
 * V2 lit les données V1 en lecture seule. Aucune écriture Firestore.
 * Aucune migration. La V1 reste opérationnelle si USE_V2_SHELL = false.
 */
import React, { useState, useMemo } from "react";
import "./v2.css";
import { useAuth } from "../auth/AuthContext";
import { loginUser } from "../auth/authService";
import AppShell from "./layout/AppShell";
import DashboardV2 from "./pages/DashboardV2";
import ObjectifsV2 from "./pages/ObjectifsV2";
import CalendrierV2 from "./pages/CalendrierV2";
import DemandesV2 from "./pages/DemandesV2";
import CampagnesV2 from "./pages/CampagnesV2";
import ProjetsTachesV2 from "./pages/ProjetsTachesV2";
import RessourcesV2 from "./pages/RessourcesV2";
import BrandCenterV2 from "./pages/BrandCenterV2";
import EquipeV2 from "./pages/EquipeV2";
import ParametresV2 from "./pages/ParametresV2";
import ThemeTournoiV2 from "./pages/ThemeTournoiV2";
import KpiV2 from "./pages/KpiV2";
import ProfilV2 from "./pages/ProfilV2";
import PublicRequestForm from "./pages/PublicRequestForm";
import PlaceholderPage from "./pages/PlaceholderPage";
import { useUsers } from "./hooks/useV1Data";
import { useRequests, filterOpenRequests, useTasks, filterTodayTasks } from "./hooks/useV1Data";

/* ── Login page ── */
// Connexion = SSO du portail Esprit Padel (cookie ep_sso partagé sur *.espritpadel.com).
// L'auto-login (AuthContext → /api/me) ouvre la session dès qu'un cookie valide existe ;
// sinon on envoie la personne se connecter sur le portail. Plus aucun mot de passe ici.
// URL du portail DÉRIVÉE DE L'HÔTE pour rester dans le bon environnement :
// comm.espritpadel.com → portail.espritpadel.com ; comm-dev.espritpadel.com →
// portail-dev.espritpadel.com. Évite qu'une connexion depuis le dev bascule en prod.
function portalUrl() {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (/^comm(-\w+)?\.espritpadel\.com$/.test(h)) return "https://" + h.replace(/^comm/, "portail");
  }
  return process.env.REACT_APP_PORTAL_URL || "https://portail.espritpadel.com";
}
const IS_DEV = process.env.NODE_ENV !== "production";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Connexion de DEV uniquement (email seul, sans portail ni mot de passe).
  // Neutralisée dans le build de production : le shim refuse hors dev.
  async function handleDevLogin(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await loginUser(email);
    } catch (err) {
      setError("Compte introuvable pour cet email.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="v2-login">
      <div className="v2-login__card">
        <div className="v2-login__logo">
          <div className="v2-login__logo-mark">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
              <path d="M4 19L9 5L14 19" stroke="#1a1a1c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 14h6" stroke="#1a1a1c" strokeWidth="2.2" strokeLinecap="round"/>
              <circle cx="19" cy="15" r="3.5" fill="#1a1a1c"/>
            </svg>
          </div>
          <h1 className="v2-login__title">Esprit Padel</h1>
          <p className="v2-login__sub">Communication OS — V2</p>
        </div>

        <p className="v2-login__sub" style={{ margin: "4px 0 18px", textAlign: "center" }}>
          Connecte-toi avec ton compte Esprit Padel via le portail.
        </p>

        <div className="v2-login__actions">
          <a className="v2-login__btn" href={portalUrl()} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
            Se connecter via le portail
          </a>
        </div>

        {IS_DEV && (
          <form onSubmit={handleDevLogin} style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
            <label className="v2-form-label" htmlFor="v2-dev-email">Connexion dev (email seul)</label>
            <input
              id="v2-dev-email"
              type="email"
              className={`v2-form-input${error ? " error" : ""}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="vous@espritpadel.com"
            />
            {error && <p className="v2-form-error">{error}</p>}
            <button type="submit" className="v2-login__btn" disabled={loading} style={{ marginTop: 10 }}>
              {loading ? <span className="v2-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : "Entrer (dev)"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ── Loading screen ── */
function LoadingScreen({ message = "Chargement…" }) {
  return (
    <div className="v2-loading">
      <div className="v2-spinner" />
      <span className="v2-loading__text">{message}</span>
    </div>
  );
}

/* ── Error screen ── */
function ErrorScreen({ message }) {
  return (
    <div className="v2-loading">
      <p style={{ color: "var(--red)", fontSize: 14, textAlign: "center", maxWidth: 320 }}>{message}</p>
    </div>
  );
}

/* ── Page title map ── */
const PAGE_TITLES = {
  dashboard:   "Dashboard",
  objectifs:   "Objectifs",
  demandes:    "Demandes clubs",
  campagnes:   "Campagnes",
  calendrier:  "Calendrier",
  contenus:    "Contenus",
  projets:     "Projets & Tâches",
  kpi:         "KPI & Reporting",
  brand:       "Brand Center",
  equipe:      "Équipe",
  ressources:  "Ressources",
  formation:   "Formation",
  parametres:  "Paramètres",
  "theme-tournoi": "Thème de tournoi",
};

/* ── Main App ── */
export default function AppV2() {
  // Formulaire public de dépôt de demandes (sans compte) : rendu AVANT toute
  // authentification quand l'URL porte `?demande`. Le paramètre ne change pas
  // sans rechargement, donc l'ordre des hooks reste stable (cf. mode sondage V1).
  let isPublicRequest = false;
  try {
    isPublicRequest = new URLSearchParams(window.location.search).has("demande");
  } catch {}
  if (isPublicRequest) {
    return (
      <div className="v2-app">
        <PublicRequestForm />
      </div>
    );
  }

  const { firebaseUser, appId, authLoading } = useAuth();
  const [currentPage, setCurrentPage] = useState("dashboard");
  // Deep-links : permettent l'ouverture directe d'une demande ou d'une campagne
  // depuis Calendrier ou Dashboard (connexion dans une passe ultérieure).
  const [selectedDemandeId,   setSelectedDemandeId]   = useState(null);
  const [selectedCampagneId,  setSelectedCampagneId]  = useState(null);

  // Read users to find current user profile + role
  const { users, loading: loadingUsers } = useUsers();
  const currentUser = useMemo(() => {
    if (!appId || !users.length) return null;
    return users.find((u) => u.id === appId || u.appId === appId) || null;
  }, [users, appId]);

  // Badges for sidebar
  const { requests } = useRequests();
  const { tasks } = useTasks();
  const openReqCount = useMemo(() => filterOpenRequests(requests).length, [requests]);
  // Mes tâches à traiter aujourd'hui (échéance ≤ aujourd'hui ou urgence haute).
  const myTodayCount = useMemo(
    () => (appId ? filterTodayTasks(tasks, appId).length : 0),
    [tasks, appId]
  );
  const badges = useMemo(() => ({
    demandes: openReqCount,
    projets: myTodayCount,
  }), [openReqCount, myTodayCount]);

  // ── States ──
  if (authLoading) return (
    <div className="v2-app">
      <LoadingScreen message="Connexion en cours…" />
    </div>
  );

  if (!firebaseUser) return (
    <div className="v2-app">
      <LoginPage />
    </div>
  );

  if (!authLoading && !appId) return (
    <div className="v2-app">
      <ErrorScreen message="Votre compte n'est pas encore configuré. Contactez l'administrateur." />
    </div>
  );

  // ── App shell ──
  function renderPage() {
    if (currentPage === "dashboard") {
      return (
        <DashboardV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
        />
      );
    }
    if (currentPage === "objectifs") {
      return (
        <ObjectifsV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
        />
      );
    }
    // « evenements » est fusionné dans le calendrier unifié : on redirige.
    if (currentPage === "calendrier" || currentPage === "evenements") {
      return (
        <CalendrierV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
        />
      );
    }
    if (currentPage === "demandes") {
      return (
        <DemandesV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
          initialSelectedId={selectedDemandeId}
        />
      );
    }
    if (currentPage === "campagnes") {
      return (
        <CampagnesV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
          initialSelectedId={selectedCampagneId}
        />
      );
    }
    if (currentPage === "projets") {
      return (
        <ProjetsTachesV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
          initialSelectedCampagneId={selectedCampagneId}
        />
      );
    }
    if (currentPage === "ressources") {
      return (
        <RessourcesV2
          appId={appId}
          currentUser={currentUser}
        />
      );
    }
    if (currentPage === "brand") {
      return (
        <BrandCenterV2
          appId={appId}
          currentUser={currentUser}
        />
      );
    }
    if (currentPage === "equipe") {
      return (
        <EquipeV2
          appId={appId}
          currentUser={currentUser}
        />
      );
    }
    if (currentPage === "kpi") {
      return (
        <KpiV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
        />
      );
    }
    if (currentPage === "profil") {
      return (
        <ProfilV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
        />
      );
    }
    if (currentPage === "parametres") {
      return (
        <ParametresV2
          appId={appId}
          currentUser={currentUser}
          onNavigate={setCurrentPage}
        />
      );
    }
    if (currentPage === "theme-tournoi") {
      return <ThemeTournoiV2 />;
    }
    return (
      <PlaceholderPage
        pageId={currentPage}
        title={PAGE_TITLES[currentPage] || currentPage}
      />
    );
  }

  return (
    <div className="v2-app">
      <AppShell
        currentPage={currentPage}
        onNavigate={setCurrentPage}
        badges={badges}
        currentUser={currentUser}
        appId={appId}
      >
        {renderPage()}
      </AppShell>
    </div>
  );
}
